import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiRequestJson } from '@/lib/queryClient';
import { useTRPC, useTRPCClient } from '@/lib/trpc';
import { getCurrentTime } from '@/utils/timeUtils';
import { recordRecentLeague } from '@/lib/storage';
import { createRandomPickForBet } from '@shared/randomPicks';
import { hasLeagueWeekStarted } from '@shared/pickLocking';
import { filterPastAndActiveWeeks } from '@shared/weeks';
import {
  getLeagueHref,
  normalizeStringParam,
  type LeaguePrimarySection,
} from '@/lib/routes';

export type LeagueGameBet = {
  id: string;
  leagueWeekBetId?: string | null;
  betType: 'moneyline' | 'spread' | 'total' | 'total_tiebreaker' | 'custom_prop';
  oddsOne?: number | null;
  oddsTwo?: number | null;
  displayInfo?: Record<string, unknown> | null;
  cutoffAt?: string | null;
  pickLockCutoffAt?: string | null;
  locked?: boolean;
  scoringConfigOverride?: Record<string, unknown> | null;
};

export type LeagueWeekGameGroup = {
  game: any;
  bets: LeagueGameBet[];
};

export type LeagueWeekGamesPayload = {
  leagueWeekId: string | null;
  resolvedSeasonId: string;
  legacy?: boolean;
  games: LeagueWeekGameGroup[];
};

type RandomPickCandidate = {
  game: any;
  bet: LeagueGameBet;
};

export type LeagueSeasonWeek = {
  id: string;
  leagueWeekId: string;
  label: string;
  displayLabel: string;
  weekNumber: number | null;
  roundKey?: string | null;
  roundLabel?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
};

export type LeagueSeasonWeeksPayload = {
  resolvedSeasonId: string;
  weeks: LeagueSeasonWeek[];
};

export type PicksQueryKey = readonly unknown[];

const buildBetKey = (gameId: string, bet: LeagueGameBet) => {
  if (bet.leagueWeekBetId) {
    return bet.leagueWeekBetId;
  }
  if (bet.id) {
    return bet.id;
  }
  return `${gameId}-${bet.betType}`;
};

const isBetLocked = (bet: LeagueGameBet | undefined, gameTime?: string | null, now: Date = getCurrentTime()) => {
  if (bet?.locked === true) return true;
  const cutoffValue = bet?.pickLockCutoffAt ?? bet?.cutoffAt ?? gameTime;
  if (!cutoffValue) return false;
  const cutoff = new Date(cutoffValue);
  return !Number.isNaN(cutoff.getTime()) && cutoff <= now;
};

const seasonSessionCache = new Map<string, string>();
const MARCH_MADNESS_PREFETCH_STALE_TIME_MS = 30_000;

export function useLeague(leagueIdProp?: string) {
  const isSaving = useIsMutating({ mutationKey: ['submitPick'] });
  const router = useRouter();
  const routeParams = useGlobalSearchParams<{
    id?: string | string[];
    season?: string | string[];
    week?: string | string[];
    mmRound?: string | string[];
  }>();
  
  const id = leagueIdProp ?? normalizeStringParam(routeParams.id) ?? '';
  const isInvalidId = !id || id === 'undefined';
  const seasonParam = normalizeStringParam(routeParams.season) ?? null;
  const weekParam = normalizeStringParam(routeParams.week) ?? null;
  const mmRoundParam = normalizeStringParam(routeParams.mmRound) ?? null;

  const [stickySeasonId, setStickySeasonId] = useState<string | null>(() => {
    return seasonParam ?? (id && !isInvalidId ? seasonSessionCache.get(id) : null) ?? null;
  });
  const stickySeasonLeagueIdRef = useRef(id);

  // useLeague lives above the native Tabs navigator, so it can survive a
  // league switch. Never let the previous league's sticky season leak into a
  // query for the new league while the route transition is settling.
  const stickySeasonIdForLeague =
    stickySeasonLeagueIdRef.current === id
      ? stickySeasonId
      : (id && !isInvalidId ? seasonSessionCache.get(id) : null) ?? null;

  useEffect(() => {
    if (stickySeasonLeagueIdRef.current === id) {
      return;
    }

    stickySeasonLeagueIdRef.current = id;
    const nextSeasonId =
      seasonParam ?? (id && !isInvalidId ? seasonSessionCache.get(id) : null) ?? null;
    setStickySeasonId(nextSeasonId);
  }, [id, isInvalidId, seasonParam]);

  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const lastRecordedLeagueIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (id && !isInvalidId && lastRecordedLeagueIdRef.current !== id) {
      lastRecordedLeagueIdRef.current = id;
      void recordRecentLeague(id);
    }
  }, [id, isInvalidId]);

  const leagueDetailInput = useMemo(
    () => ({
      leagueId: id,
      seasonId: seasonParam ?? stickySeasonIdForLeague ?? undefined,
    }),
    [id, seasonParam, stickySeasonIdForLeague],
  );

  const leagueQuery = useQuery(
    trpc.leagues.detail.queryOptions(leagueDetailInput, {
      enabled: isAuthenticated && !isInvalidId,
      retry: false,
      staleTime: (query) => {
        // Auth summaries also prime this detail cache. If one lacks this field,
        // refetch immediately so tab visibility uses the league's real setting.
        const chatEnabled = (query.state.data as { chatEnabled?: unknown } | undefined)?.chatEnabled;
        return typeof chatEnabled === 'boolean' ? 60_000 : 0;
      },
    }),
  );
  const league = leagueQuery.data as any;
  const leagueLoading =
    leagueQuery.isPending ||
    (leagueQuery.isFetching &&
      (league?.id !== id || typeof league?.chatEnabled !== 'boolean'));

  useEffect(() => {
    if (id && !isInvalidId) {
      if (seasonParam) {
        if (seasonParam !== stickySeasonId) {
          setStickySeasonId(seasonParam);
          seasonSessionCache.set(id, seasonParam);
        }
      } else if (league?.resolvedSeasonId) {
        if (league.resolvedSeasonId !== stickySeasonId) {
          setStickySeasonId(league.resolvedSeasonId);
          seasonSessionCache.set(id, league.resolvedSeasonId);
        }
      }
    }
  }, [seasonParam, league?.resolvedSeasonId, id, isInvalidId, stickySeasonId]);

  const resolvedSeasonId =
    seasonParam ??
    stickySeasonIdForLeague ??
    (league?.id === id ? league.resolvedSeasonId : null) ??
    null;

  const {
    data: currentWeek,
  } = useQuery(
    trpc.nfl.currentWeek.queryOptions(undefined, {
      enabled: isAuthenticated && !isInvalidId,
      staleTime: 60_000,
    }),
  );

  const { data: leagueSeasons } = useQuery(
    trpc.seasons.listByLeague.queryOptions(
      { leagueId: id },
      {
        enabled: isAuthenticated && !isInvalidId,
        staleTime: 60_000,
      },
    ),
  );

  const seasonWeeksQuery = useQuery(
    trpc.leagues.seasonWeeks.queryOptions(
      {
        leagueId: id,
        seasonId: resolvedSeasonId ?? undefined,
      },
      {
        enabled: isAuthenticated && !isInvalidId && !!resolvedSeasonId,
        staleTime: 60_000,
      },
    ),
  );
  const seasonWeeks = seasonWeeksQuery.data as LeagueSeasonWeeksPayload | undefined;
  const seasonWeeksLoading = seasonWeeksQuery.isLoading;

  const allSeasonWeekOptions = useMemo(() => seasonWeeks?.weeks ?? [], [seasonWeeks]);

  const currentSeasonTemplateId = useMemo(() => {
    const info = league?.currentSeason?.additionalInfo;
    if (info && typeof info === 'object' && info !== null && 'fromTemplate' in info) {
      const templateValue = (info as { fromTemplate?: unknown }).fromTemplate;
      if (typeof templateValue === 'string') {
        return templateValue;
      }
    }
    return undefined;
  }, [league?.currentSeason?.additionalInfo]);

  const isPlayoffSeason = useMemo(() => {
    if (currentSeasonTemplateId?.toLowerCase().includes('playoff')) {
      return true;
    }
    const hasRegularWeeks = allSeasonWeekOptions.some(
      (week) => typeof week.weekNumber === 'number' && week.weekNumber <= 18,
    );
    const hasPostSeasonWeeks = allSeasonWeekOptions.some(
      (week) => typeof week.weekNumber === 'number' && week.weekNumber > 18,
    );
    return !hasRegularWeeks && hasPostSeasonWeeks;
  }, [allSeasonWeekOptions, currentSeasonTemplateId]);

  const seasonWeekOptions = useMemo(() => {
    if (isPlayoffSeason) {
      return allSeasonWeekOptions;
    }
    return allSeasonWeekOptions.filter((week) => {
      if (typeof week.weekNumber === 'number') {
        return week.weekNumber <= 18;
      }
      return true;
    });
  }, [allSeasonWeekOptions, isPlayoffSeason]);

  const pickerWeekOptions = useMemo(
    () => filterPastAndActiveWeeks(seasonWeekOptions, currentWeek, getCurrentTime()),
    [currentWeek, seasonWeekOptions],
  );

  const currentWeekId = currentWeek?.id ?? null;
  const [dirtyTiebreakerKeys, setDirtyTiebreakerKeys] = useState<Set<string>>(new Set());
  
  const defaultWeek = useMemo(() => {
    if (!pickerWeekOptions.length) {
      return null;
    }
    const matchingCurrent = currentWeekId
      ? pickerWeekOptions.find((week) => week.id === currentWeekId)
      : null;
    return matchingCurrent ?? pickerWeekOptions[0] ?? null;
  }, [currentWeekId, pickerWeekOptions]);

  const activeWeek = useMemo(() => {
    if (!pickerWeekOptions.length) {
      return null;
    }
    if (weekParam) {
      const matchingWeek = pickerWeekOptions.find((week) => week.id === weekParam);
      if (matchingWeek) {
        return matchingWeek;
      }
    }
    return defaultWeek;
  }, [defaultWeek, pickerWeekOptions, weekParam]);

  const pickerSelectedValue = activeWeek?.id ?? '';

  const handleSeasonChange = useCallback((value: string, currentSection: LeaguePrimarySection = 'picks') => {
    if (!id) return;
    router.replace(
      getLeagueHref(String(id), currentSection, {
        seasonId: value,
        weekId: null,
      }),
    );
  }, [id, router]);

  const handleWeekChange = useCallback((value: string, currentSection: LeaguePrimarySection = 'picks') => {
    if (!id) return;
    router.replace(
      getLeagueHref(String(id), currentSection, {
        seasonId: seasonParam,
        weekId: value,
      }),
    );
  }, [id, router, seasonParam]);

  const picksQueryInput = useMemo(
    () => ({
      leagueId: id ?? '',
      weekId: activeWeek?.id ?? '',
      seasonId: resolvedSeasonId ?? undefined,
    }),
    [id, activeWeek?.id, resolvedSeasonId],
  );

  const picksQueryKey = useMemo<PicksQueryKey>(
    () => trpc.picks.list.queryKey(picksQueryInput),
    [trpc, picksQueryInput],
  );

  useEffect(() => {
    setDirtyTiebreakerKeys(new Set());
  }, [activeWeek?.id]);

  const {
    data: userPicks,
    isLoading: userPicksLoading,
  } = useQuery(
    trpc.picks.list.queryOptions(picksQueryInput, {
      enabled: isAuthenticated && !isInvalidId && !!activeWeek?.id && !!id && !!resolvedSeasonId,
      staleTime: 60_000,
    }),
  );

  const leagueWeekGamesQuery = useQuery(
    trpc.leagues.weekGames.queryOptions(
      {
        leagueId: id,
        weekId: activeWeek?.id ?? '',
        seasonId: resolvedSeasonId ?? undefined,
      },
      {
        enabled: isAuthenticated && !isInvalidId && !!activeWeek?.id && !!id && !!resolvedSeasonId,
        staleTime: 60_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
      },
    ),
  );
  const leagueWeekGames = leagueWeekGamesQuery.data as LeagueWeekGamesPayload | undefined;
  const leagueWeekGamesLoading = leagueWeekGamesQuery.isLoading;

  const gameGroups = useMemo<LeagueWeekGameGroup[]>(() => leagueWeekGames?.games ?? [], [leagueWeekGames]);
  const sortedGames = useMemo(() => {
    if (!gameGroups.length) return [];
    return [...gameGroups].sort((a, b) => {
      const timeDiff = new Date(a.game.gameTime).getTime() - new Date(b.game.gameTime).getTime();
      if (timeDiff !== 0) return timeDiff;
      const homeA = a.game.homeTeam?.name || a.game.homeTeam?.abbreviation || '';
      const homeB = b.game.homeTeam?.name || b.game.homeTeam?.abbreviation || '';
      return homeA.localeCompare(homeB);
    });
  }, [gameGroups]);

  const permissionsQuery = useQuery(
    trpc.leagues.permissions.queryOptions(
      { leagueId: id },
      {
        enabled: isAuthenticated && !isInvalidId,
        staleTime: 60_000,
      },
    ),
  );
  const permissions = permissionsQuery.data as
    | { isOwner: boolean; isManager: boolean; canInvite: boolean; canLeave: boolean }
    | undefined;
  const permissionsLoading = permissionsQuery.isLoading;

  const randomPicksMutation = useMutation({
    mutationFn: async (picks: any[]) => {
      const pickPromises = picks.map((pick) => trpcClient.picks.submit.mutate(pick));
      return Promise.all(pickPromises);
    },
    onSuccess: () => {
      toast.show({ type: 'success', text1: 'Random picks submitted!' });
      queryClient.invalidateQueries({ queryKey: picksQueryKey });
      queryClient.invalidateQueries(trpc.leagues.achievements.queryFilter());
    },
    onError: (error) => {
      toast.show({ type: 'error', text1: 'Error', text2: error instanceof Error ? error.message : 'Failed to submit random picks' });
    }
  });

  const clearPicksMutation = useMutation({
    mutationFn: async (pickIds: string[]) => {
      const deletePromises = pickIds.map((pid) => trpcClient.picks.delete.mutate({ pickId: pid }));
      return Promise.all(deletePromises);
    },
    onSuccess: () => {
      toast.show({ type: 'success', text1: 'Picks cleared!' });
      queryClient.invalidateQueries({ queryKey: picksQueryKey });
    },
  });

  const userPicksByBetId = useMemo(() => {
    const map = new Map<string, { pick: any; game: any }>();
    (userPicks ?? []).forEach((item: any) => {
      const pick = item.picks || item;
      const game = item.nfl_games;
      const betKey = pick.leagueWeekBetId ?? `${pick.gameId}-${pick.betType ?? 'moneyline'}`;
      map.set(betKey, { pick, game });
    });
    return map;
  }, [userPicks]);

  const totalBets = useMemo(() => sortedGames.reduce((sum, group) => sum + (group.bets?.length ?? 0), 0) || 0, [sortedGames]);
  const completedPicks = userPicks?.length || 0;
  
  // Use a stable timestamp that updates once per minute
  const [currentTimestamp, setCurrentTimestamp] = useState(() => Math.floor(getCurrentTime().getTime() / 60000));
  useEffect(() => {
    const timer = setInterval(() => setCurrentTimestamp(Math.floor(getCurrentTime().getTime() / 60000)), 60000);
    return () => clearInterval(timer);
  }, []);

  const clearablePicks = useMemo(() => {
    const now = new Date(currentTimestamp * 60000);
    if (
      hasLeagueWeekStarted({
        gameTimes: (sortedGames ?? []).map((group) => group?.game?.gameTime),
        now,
      })
    ) {
      return [];
    }
    return (userPicks ?? []).filter((item: any) => {
      const game = item.nfl_games;
      const pick = item.picks;
      const cutoffValue = pick?.pickLockCutoffAt ?? pick?.leagueWeekBetCutoffAt ?? game?.gameTime;
      const cutoff = cutoffValue ? new Date(cutoffValue) : null;
      return game && cutoff && !Number.isNaN(cutoff.getTime()) && cutoff > now;
    });
  }, [userPicks, currentTimestamp, sortedGames]);

  const availableForRandomPicks = useMemo<RandomPickCandidate[]>(() => {
    const now = new Date(currentTimestamp * 60000);
    return sortedGames.flatMap((group) => {
      return (group.bets ?? [])
        .filter((bet) => !isBetLocked(bet, group.game?.gameTime, now))
        .filter(bet => !userPicksByBetId.has(buildBetKey(group.game.id, bet)))
        .map((bet) => ({ game: group.game, bet }));
    });
  }, [sortedGames, currentTimestamp, userPicksByBetId]);

  const handleRandomPicks = useCallback(() => {
    if (availableForRandomPicks.length === 0 || !id || !resolvedSeasonId) return;
    const picks = availableForRandomPicks
      .filter(({ game }) => game?.id && game?.homeTeam?.id && game?.awayTeam?.id)
      .map(({ game, bet }) => {
        const randomPick = createRandomPickForBet(game, bet);
        return {
          gameId: game.id,
          leagueId: id,
          selectedTeamId: randomPick.selectedTeamId,
          side: randomPick.side,
          seasonId: resolvedSeasonId,
          leagueWeekBetId: bet.leagueWeekBetId,
          betType: bet.betType,
          tiebreakerGuess: randomPick.tiebreakerGuess,
        };
      });
    if (picks.length === 0) return;
    randomPicksMutation.mutate(picks);
  }, [availableForRandomPicks, id, resolvedSeasonId, randomPicksMutation]);

  const leagueScoringConfig = (league?.currentSeason?.scoringConfig ?? {}) as any;
  const leagueAdditionalInfo = (league?.currentSeason?.additionalInfo ?? {}) as any;
  const leagueScoringSystem = (leagueScoringConfig.legacyScoringSystem === 'totalWins' ? 'totalWins' : 'oddsBased') as 'oddsBased' | 'totalWins';
  const seasonWeekMode = leagueScoringConfig.seasonWeekMode || 'standard';

  const isMarchMadness = Boolean(
    leagueAdditionalInfo.fromTemplate === 'march-madness' ||
    league?.currentSeason?.label?.toLowerCase().includes('march madness') ||
    league?.currentSeason?.label?.toLowerCase().includes('ncaa') ||
    leagueScoringConfig.mmEnabled === true ||
    (seasonParam && seasonParam.toLowerCase().includes('mm'))
  );

  const isWorldCupTournament = Boolean(
    currentSeasonTemplateId?.toLowerCase().includes('world-cup') ||
    league?.currentSeason?.label?.toLowerCase().includes('world cup') ||
    league?.currentSeason?.label?.toLowerCase().includes('fifa world cup')
  );

  const mmSeasonInput = useMemo(
    () => ({ leagueId: id, seasonId: resolvedSeasonId ?? '' }),
    [id, resolvedSeasonId],
  );
  const mmQueriesEnabled = isAuthenticated && isMarchMadness && !isInvalidId && !!resolvedSeasonId;

  const { data: mmStatus, isLoading: mmStatusLoading } = useQuery(
    trpc.marchMadness.status.queryOptions(mmSeasonInput, {
      enabled: mmQueriesEnabled,
      staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS,
    }),
  );

  const { data: mmDraftState, isLoading: mmDraftLoading } = useQuery(
    trpc.marchMadness.draft.queryOptions(mmSeasonInput, {
      enabled: mmQueriesEnabled && Boolean(mmStatus?.hasSetup),
      staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS,
      refetchInterval: (query) => {
        const status = (query.state.data as { draft?: { status?: string } } | undefined)?.draft?.status;
        return status === 'active' ? 2000 : false;
      },
      refetchIntervalInBackground: true,
    }),
  );

  const { data: mmOverview, isLoading: mmOverviewLoading } = useQuery(
    trpc.marchMadness.overview.queryOptions(mmSeasonInput, {
      enabled: mmQueriesEnabled && Boolean(mmStatus?.hasSetup),
      staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS,
      refetchInterval: () => {
        const status = (mmDraftState as { draft?: { status?: string } } | undefined)?.draft?.status;
        return status === 'active' ? 5000 : 15000;
      },
      refetchIntervalInBackground: true,
    }),
  );

  const mmSelectedRoundKey = useMemo(() => {
    if (!mmOverview?.rounds?.length) {
      return null;
    }
    if (mmRoundParam && mmOverview.rounds.some((round: any) => round.roundKey === mmRoundParam)) {
      return mmRoundParam;
    }

    const activeRound = mmOverview.rounds.find((round: any) => !round.isFinal);
    if (activeRound) {
      return activeRound.roundKey;
    }

    const mostRecentlyCompletedRound = mmOverview.rounds[mmOverview.rounds.length - 1];
    return mostRecentlyCompletedRound?.roundKey ?? mmOverview.rounds[0]?.roundKey ?? null;
  }, [mmRoundParam, mmOverview?.rounds]);

  const { data: mmBracket } = useQuery(
    trpc.marchMadness.bracket.queryOptions(mmSeasonInput, {
      enabled: mmQueriesEnabled && Boolean(mmStatus?.hasSetup),
      staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS,
      placeholderData: (previousData) => previousData,
      refetchInterval: 30_000,
      refetchIntervalInBackground: true,
    }),
  );

  const { data: mmSelectedRoundDetail } = useQuery(
    trpc.marchMadness.round.queryOptions(
      { ...mmSeasonInput, roundKey: mmSelectedRoundKey ?? '' },
      {
        enabled: mmQueriesEnabled && Boolean(mmStatus?.hasSetup) && !!mmSelectedRoundKey,
        staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS,
        placeholderData: (previousData) => previousData,
        refetchInterval: 15_000,
        refetchIntervalInBackground: true,
      },
    ),
  );

  useEffect(() => {
    if (mmQueriesEnabled && mmStatus?.hasSetup && mmOverview?.rounds) {
      for (const round of mmOverview.rounds) {
        if (round.roundKey === mmSelectedRoundKey) continue;
        void queryClient.prefetchQuery(
          trpc.marchMadness.round.queryOptions(
            { ...mmSeasonInput, roundKey: round.roundKey },
            { staleTime: MARCH_MADNESS_PREFETCH_STALE_TIME_MS },
          ),
        );
      }
    }
  }, [mmQueriesEnabled, mmSeasonInput, mmStatus?.hasSetup, mmOverview?.rounds, queryClient, mmSelectedRoundKey, trpc.marchMadness.round]);

  useEffect(() => {
    if (!isAuthenticated || isInvalidId || !id || !resolvedSeasonId || isMarchMadness) {
      return;
    }

    const params = new URLSearchParams();
    params.set('seasonId', resolvedSeasonId);
    const seasonQuery = `?${params.toString()}`;
    const effectiveWeekNumber = typeof activeWeek?.weekNumber === 'number' ? activeWeek.weekNumber : null;
    const selectedWeekId = activeWeek?.id ?? null;
    const showPaymentsTab = Boolean(permissions?.isOwner || permissions?.isManager);

    void queryClient.prefetchQuery(
      trpc.leagues.leaderboard.queryOptions({
        leagueId: id,
        seasonId: resolvedSeasonId,
      }),
    );

    void queryClient.prefetchQuery({
      queryKey: ['/api/leagues', id, 'season-weeks', resolvedSeasonId, 'leaderboard'],
      queryFn: () =>
        apiRequestJson<LeagueSeasonWeeksPayload>(`/api/leagues/${id}/weeks${seasonQuery}`, 'GET'),
    });

    void queryClient.prefetchQuery({
      queryKey: ['/api/leagues', id, 'season-weeks', resolvedSeasonId],
      queryFn: () =>
        apiRequestJson<LeagueSeasonWeeksPayload>(`/api/leagues/${id}/weeks${seasonQuery}`, 'GET'),
    });

    void queryClient.prefetchQuery(
      trpc.leagues.members.queryOptions({
        leagueId: id,
        seasonId: resolvedSeasonId,
      }),
    );

    if (selectedWeekId) {
      void queryClient.prefetchQuery({
        queryKey: ['/api/leagues', id, 'weeks', selectedWeekId, resolvedSeasonId, 'scores'],
        queryFn: () =>
          apiRequestJson<LeagueWeekGamesPayload>(
            `/api/leagues/${id}/weeks/${selectedWeekId}/games${seasonQuery}`,
            'GET',
          ),
      });

      const propDefinitionIds = gameGroups
        .flatMap((group) => group.bets ?? [])
        .map((bet: any) => bet?.propDefinitionId)
        .filter((value): value is string => typeof value === 'string' && value.length > 0);

      if (propDefinitionIds.length > 0) {
        void queryClient.prefetchQuery(
          trpc.leagues.propDefinitions.queryOptions({
            leagueId: id,
            weekId: selectedWeekId,
            seasonId: resolvedSeasonId,
          }),
        );

        void queryClient.prefetchQuery(
          trpc.leagues.propEvaluations.queryOptions({
            leagueId: id,
            weekId: selectedWeekId,
            seasonId: resolvedSeasonId,
          }),
        );
      }
    }

    if (effectiveWeekNumber !== null) {
      void queryClient.prefetchQuery(
        trpc.leagues.picksByWeek.queryOptions({
          leagueId: id,
          week: effectiveWeekNumber,
          seasonId: resolvedSeasonId,
        }),
      );

      if (seasonWeekMode === 'propWeeklyOnly') {
        void queryClient.prefetchQuery(
          trpc.leagues.leaderboard.queryOptions({
            leagueId: id,
            seasonId: resolvedSeasonId,
            weekNumber: effectiveWeekNumber,
          }),
        );
      }
    }

    if (showPaymentsTab) {
      void queryClient.prefetchQuery(
        trpc.leagues.paymentSummary.queryOptions({
          leagueId: id,
          seasonId: resolvedSeasonId,
        }),
      );

      void queryClient.prefetchQuery(trpc.nfl.weeks.queryOptions());
    }

    if (user?.id) {
      void queryClient.prefetchQuery(
        trpc.users.stats.queryOptions({
          userId: user.id,
          leagueId: id,
          seasonId: resolvedSeasonId,
        }),
      );

      void queryClient.prefetchQuery({
        queryKey: ['/api/leagues', id, 'season-weeks', resolvedSeasonId, 'stats'],
        queryFn: () =>
          apiRequestJson<LeagueSeasonWeeksPayload>(`/api/leagues/${id}/weeks${seasonQuery}`, 'GET'),
      });
    }
  }, [
    activeWeek?.id,
    activeWeek?.weekNumber,
    id,
    isAuthenticated,
    isInvalidId,
    isMarchMadness,
    gameGroups,
    permissions?.isManager,
    permissions?.isOwner,
    queryClient,
    resolvedSeasonId,
    seasonWeekMode,
    trpc.leagues.leaderboard,
    trpc.leagues.members,
    trpc.leagues.paymentSummary,
    trpc.leagues.picksByWeek,
    trpc.leagues.propDefinitions,
    trpc.leagues.propEvaluations,
    trpc.nfl.weeks,
    trpc.users.stats,
    user?.id,
  ]);

  const isLoading = Boolean(
    authLoading ||
    leagueLoading ||
    permissionsLoading ||
    seasonWeeksLoading ||
    (isMarchMadness && (mmStatusLoading || (mmStatus?.hasSetup && (mmOverviewLoading || mmDraftLoading)))),
  );

  return useMemo(() => ({
    id,
    resolvedSeasonId,
    league,
    leagueLoading,
    leagueSeasons: leagueSeasons ?? [],
    permissions,
    permissionsLoading,
    leagueScoringSystem,
    seasonWeekMode,
    seasonWeekOptions,
    pickerWeekOptions,
    activeWeek,
    handleWeekChange,
    handleSeasonChange,
    pickerSelectedValue,
    isSaving,
    dirtyTiebreakerKeys,
    setDirtyTiebreakerKeys,
    completedPicks,
    totalPickTargets: totalBets,
    clearablePicks,
    availableForRandomPicks,
    clearPicksMutation,
    randomPicksMutation,
    handleRandomPicks,
    initialPicksLoading: userPicksLoading && !userPicks,
    sortedGames,
    gamesLoading: leagueWeekGamesLoading,
    userPicksByBetId,
    picksQueryKey,
    isAuthenticated,
    isMarchMadness,
    isWorldCupTournament,
    mmStatus,
    mmDraftState,
    mmOverview,
    mmBracket,
    mmSelectedRoundDetail,
    mmSelectedRoundKey,
    isInvalidId,
    isLoading,
  }), [
    id,
    resolvedSeasonId,
    league,
    leagueLoading,
    leagueSeasons,
    permissions,
    permissionsLoading,
    leagueScoringSystem,
    seasonWeekMode,
    seasonWeekOptions,
    pickerWeekOptions,
    activeWeek,
    handleWeekChange,
    handleSeasonChange,
    pickerSelectedValue,
    isSaving,
    dirtyTiebreakerKeys,
    completedPicks,
    totalBets,
    clearablePicks,
    availableForRandomPicks,
    clearPicksMutation,
    randomPicksMutation,
    handleRandomPicks,
    userPicksLoading,
    userPicks,
    sortedGames,
    leagueWeekGamesLoading,
    userPicksByBetId,
    picksQueryKey,
    isAuthenticated,
    isMarchMadness,
    isWorldCupTournament,
    mmStatus,
    mmDraftState,
    mmOverview,
    mmBracket,
    mmSelectedRoundDetail,
    mmSelectedRoundKey,
    isInvalidId,
    isLoading
  ]);
}
